import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { CheckInRecord } from './types';

const PrintConfirmDialog: React.FC<{
  kids: CheckInRecord[];
  requestedBy: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ kids, requestedBy, onConfirm, onCancel }) => {
  const [open, setOpen] = useState(true);

  const handleConfirm = () => {
    setOpen(false);
    onConfirm();
  };

  const handleCancel = () => {
    setOpen(false);
    onCancel();
  };

  const names = kids
    .map(
      (c) =>
        `${c.childFirstName} ${requestedBy === 'admin' ? c.childLastName : ''}`
    )
    .join(', ');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Print Name Tags</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Name tags will be printed for: <br />
          <strong>{names}</strong>. <br />
          Pick up the name tags at the check-in desk.
          {requestedBy !== 'admin' && (
            <>
              {` `}Don{"'t"} forget your pickup pin:{' '}
              <strong>{kids[0].pickupPin}</strong>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function showConfirmDialog(
  kids: CheckInRecord[],
  requestedBy: string
): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = (result: boolean) => {
      root.unmount();
      document.body.removeChild(container);
      resolve(result);
    };

    root.render(
      <PrintConfirmDialog
        kids={kids}
        requestedBy={requestedBy}
        onConfirm={() => cleanup(true)}
        onCancel={() => cleanup(false)}
      />
    );
  });
}

export async function printLabels(
  kids: CheckInRecord[],
  requestedBy: string
): Promise<boolean> {
  if (kids.length === 0) return false;

  const date = new Date();
  const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}-${date.getFullYear()}`;

  const getAge = (dob: Date | null | undefined): number | null => {
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const toRecord = (c: CheckInRecord) => ({
    id: c.id,
    childFirstName: c.childFirstName,
    childLastName: c.childLastName,
    age: getAge(c.dateOfBirth),
    checkInTime: c.checkInTime,
    pickupPin: c.pickupPin,
    parentName: c.parentName,
  });

  const confirmed = await showConfirmDialog(kids, requestedBy);
  if (!confirmed) return false;

  const theData = {
    familyId: kids[0].familyId,
    children: kids.map(toRecord),
    requestedAt: serverTimestamp(),
    requestedBy,
    date: dateStr,
    status: 'pending',
  };
  await addDoc(collection(db, 'printQueue'), theData);
  return true;
}
